/**
 * @file PlaceGrid.tsx
 * @description Положение содержимого в родителе: сетка 3×3 и «Растянуть по
 * высоте» (phase19.16, партия SB120, этап 1; uiux-sb120-stage1
 * «Дополнение» §2.8 — как «положение контента» в Tilda; SB122 — схемы
 * вместо слов). Ряд — по вертикали, столбец — по горизонтали; ячейки 56 px
 * на касании, 48 с мышью.
 *
 * Ось, по которой двигать некуда, неактивна (`yDisabled` — высота «по
 * содержимому», `xDisabled` — ширина «Во всю»): клик меняет только
 * доступную ось. Клавиатура — как у группы радиокнопок: стрелки двигают
 * выбор по сетке; озвучка — «По центру слева».
 */

import type { SectionPlaceX, SectionPlaceY } from "@chaiberi/shared";
import { useRef, type KeyboardEvent } from "react";

import { Label } from "../../../ui/label";
import { cn } from "@/lib/utils";

type Row = Exclude<SectionPlaceY, "stretch">;

const ROWS: readonly Row[] = ["top", "center", "bottom"];
const COLS: readonly SectionPlaceX[] = ["left", "center", "right"];
const Y_NAME: Readonly<Record<Row, string>> = { top: "Сверху", center: "По центру", bottom: "Снизу" };
const X_NAME: Readonly<Record<SectionPlaceX, string>> = { left: "слева", center: "по центру", right: "справа" };

/** «По центру слева», «Сверху по центру»; центр-центр — «По центру». */
function placeName(y: SectionPlaceY, x: SectionPlaceX): string {
  if (y === "stretch") return `Растянуть по высоте, ${X_NAME[x]}`;
  return y === "center" && x === "center" ? "По центру" : `${Y_NAME[y]} ${X_NAME[x]}`;
}

interface Props {
  label: string;
  y: SectionPlaceY;
  x: SectionPlaceX;
  onChange: (place: { y: SectionPlaceY; x: SectionPlaceX }) => void;
  yDisabled?: boolean;
  xDisabled?: boolean;
}

export function PlaceGrid({ label, y, x, onChange, yDisabled = false, xDisabled = false }: Props) {
  const stretch = y === "stretch";
  const row = stretch ? 1 : ROWS.indexOf(y);
  const col = COLS.indexOf(x);
  const all = yDisabled && xDisabled;
  const grid = useRef<HTMLDivElement | null>(null);

  function pick(r: number, c: number) {
    const nextY = yDisabled ? y : (ROWS[r] ?? y);
    const nextX = xDisabled ? x : (COLS[c] ?? x);
    onChange({ y: stretch && yDisabled ? y : nextY, x: nextX });
  }
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const d: Record<string, [number, number]> = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };
    const step = d[event.key];
    if (!step) return;
    event.preventDefault();
    const r = Math.min(2, Math.max(0, row + (yDisabled ? 0 : step[0])));
    const c = Math.min(2, Math.max(0, col + (xDisabled ? 0 : step[1])));
    if (r === row && c === col) return;
    pick(r, c);
    // Фокус — на новую ячейку, как у группы радиокнопок.
    grid.current?.querySelectorAll<HTMLButtonElement>("[role=\"radio\"]")[r * 3 + c]?.focus();
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap items-start gap-3">
        <div ref={grid} role="radiogroup" aria-label={label} aria-disabled={all || undefined} onKeyDown={onKeyDown} className="grid w-max grid-cols-3 gap-1 rounded-xl bg-surface-low p-1">
          {ROWS.flatMap((ry, r) =>
            COLS.map((cx, c) => {
              const active = c === col && (stretch || r === row);
              const focusable = c === col && r === row;
              return (
                <button
                  key={`${ry}-${cx}`}
                  type="button"
                  role="radio"
                  aria-checked={c === col && r === row}
                  aria-label={placeName(ry, cx)}
                  title={placeName(ry, cx)}
                  tabIndex={focusable && !all ? 0 : -1}
                  disabled={all || (yDisabled && r !== row) || (xDisabled && c !== col)}
                  onClick={() => pick(r, c)}
                  className={cn(
                    "flex size-14 items-center justify-center rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring can-hover:size-12 disabled:cursor-not-allowed disabled:opacity-50",
                    active ? "bg-surface-lowest [box-shadow:inset_0_0_0_1px_var(--outline-variant)]" : "hover:bg-surface-lowest/60",
                  )}
                >
                  <span aria-hidden className={cn("rounded-sm", active ? "bg-primary" : "bg-on-surface-variant/40", stretch && active ? "h-full w-2.5" : "size-2.5")} />
                </button>
              );
            }),
          )}
        </div>
        <button
          type="button"
          aria-pressed={stretch}
          disabled={yDisabled}
          onClick={() => onChange({ y: stretch ? "center" : "stretch", x })}
          className="inline-flex h-14 items-center rounded-lg bg-surface-low px-3 text-sm font-medium text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-ring aria-pressed:[box-shadow:inset_0_0_0_2px_var(--primary)] disabled:cursor-not-allowed disabled:opacity-50 can-hover:h-12"
        >
          Растянуть по высоте
        </button>
      </div>
    </div>
  );
}
